import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/lib/contexts/auth-context";
import { apiClient } from "@/lib/api/client";
import { BlockDto } from "@/lib/types/api";
import { handleApiError } from "@/lib/utils/errors";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import IndicatorDataEntry from "@/components/indicator-data/indicator-data-entry";

export default function BlockIndicatorsPage() {
  const [block, setBlock] = useState<BlockDto | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const { blockId } = useParams<{ blockId: string }>();
  const { user } = useAuth();

  useEffect(() => {
    if (!blockId) {
      navigate("/dashboard");
      return;
    }

    const isUser = user?.roles?.includes("User");
    const hasBlock = user?.blockId;

    // User with block assignment - can only access their assigned block
    if (isUser && hasBlock && user.blockId !== blockId) {
      navigate("/dashboard");
      return;
    }

    loadBlock();
  }, [blockId, user, navigate]);

  const loadBlock = async () => {
    if (!blockId) return;

    try {
      setIsLoading(true);
      const blockRes = await apiClient.getBlockById(blockId);
      if (blockRes.success && blockRes.data) {
        setBlock(blockRes.data);
      }
    } catch (err: unknown) {
      // Silently fail - will show in loading state
      console.error(handleApiError(err, "Failed to load block"));
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return <div className="flex justify-center py-8">Loading...</div>;
  }

  if (!block || !blockId) {
    return (
      <div className="space-y-6">
        <Button variant="outline" size="sm" onClick={() => navigate(`/dashboard/indicator-data/block/${blockId}`)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Block Details
        </Button>
        <div className="text-center py-8 text-gray-600 dark:text-gray-400">Block not found</div>
      </div>
    );
  }

  return (
    <IndicatorDataEntry
      locationId={blockId}
      locationType="block"
      locationName={block.blockName}
    />
  );
}
